import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useFamilyStore } from '../store/familyStore';
import { useEventStore } from '../store/eventStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { useNavigate, useLocation } from 'react-router-dom';
import { addMonths, addDays, formatDate, isToday } from '../utils/calendar';
import { CalendarView, CalendarEvent, RecurringPattern } from '../types/calendar';
import MonthView from '../components/calendar/MonthView';
import WeekView from '../components/calendar/WeekView';
import DayView from '../components/calendar/DayView';
import EventPreview from '../components/calendar/EventPreview';
import NavBar from '../components/NavBar';
import CreateEventWizard, { EventFormData } from '../components/CreateEventWizard';
import ConfirmDialog from '../components/ConfirmDialog';
import { getBaseEventId, isExpandedRecurringInstance } from '../utils/recurringInstance';
import { Icon } from '../utils/icons';
import { enrichEventsWithFamilyData } from '../utils/enrichEvents';
import { CoreNavRail } from '../components/CoreNavRail';
import { EventTodoPanel } from '../components/todos/EventTodoPanel';
import DatePicker from '../components/DatePicker';
import { useTodoStore } from '../store/todoStore';
import { useToastStore } from '../store/toastStore';

export default function Calendar() {
  const { isAuthenticated } = useAuthStore();
  const { family, fetchFamily, isLoading: isFamilyLoading } = useFamilyStore();
  const { showToast, dismissToast } = useToastStore();
  const rawEvents = useEventStore((state) => state.events);
  
  // Enrich events with current family member data (colors, names, avatars)
  // Use a serialized version of member data to ensure useMemo recalculates when colors change
  const memberDataKey = family?.members ? JSON.stringify(family.members.map(m => ({ userId: m.userId, color: m.color, name: m.name, avatar: m.avatar }))) : '';
  const events = useMemo(() => {
    if (!family?.members) {
      return rawEvents;
    }
    const enriched = enrichEventsWithFamilyData(rawEvents, family.members);
    return enriched;
  }, [rawEvents, memberDataKey]);
  
  const fetchEvents = useEventStore((state) => state.fetchEvents);
  const createEvent = useEventStore((state) => state.createEvent);
  const updateEvent = useEventStore((state) => state.updateEvent);
  const deleteEvent = useEventStore((state) => state.deleteEvent);
  const isCreating = useEventStore((state) => state.isCreating);
  const isUpdating = useEventStore((state) => state.isUpdating);
  const isDeleting = useEventStore((state) => state.isDeleting);
  const isLoadingEvents = useEventStore((state) => state.isLoading);
  
  // Only show overlay on initial load when no events are loaded yet
  const hasEventsLoaded = events.length > 0;
  const showLoadingOverlay = isLoadingEvents && !hasEventsLoaded;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track last fetch to prevent duplicate calls
  const lastFetchRef = useRef<{ key: string } | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('day');
  
  // Handle navigation state for "Today" nav item
  const hasProcessedNavState = useRef(false);
  useEffect(() => {
    const navState = location.state as { view?: CalendarView; date?: Date | string } | null;
    if (navState?.view === 'day' && navState?.date && !hasProcessedNavState.current) {
      const targetDate = typeof navState.date === 'string' ? new Date(navState.date) : navState.date;
      setCurrentDate(targetDate);
      setView('day');
      hasProcessedNavState.current = true;
      // Store in sessionStorage for active state detection in nav
      sessionStorage.setItem('calendar-view', 'day');
      sessionStorage.setItem('calendar-date', targetDate.toISOString());
      // Clear the state to prevent reprocessing on re-renders
      window.history.replaceState({}, '', location.pathname);
    }
    // Reset the ref when location changes (new navigation)
    return () => {
      hasProcessedNavState.current = false;
    };
  }, [location.state, location.pathname]);
  
  // Clear sessionStorage when view or date changes (user navigates away from today)
  useEffect(() => {
    const storedView = sessionStorage.getItem('calendar-view');
    const storedDate = sessionStorage.getItem('calendar-date');
    if (storedView === 'day' && storedDate) {
      const storedDateObj = new Date(storedDate);
      // Clear if view changed or date is no longer today
      if (view !== 'day' || !isToday(currentDate) || !isToday(storedDateObj)) {
        sessionStorage.removeItem('calendar-view');
        sessionStorage.removeItem('calendar-date');
      }
    }
  }, [view, currentDate]);
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [selectedMemberFilter, setSelectedMemberFilter] = useState<string | null>(null); // null = "All", userId = specific member
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showEventPreview, setShowEventPreview] = useState(false);
  const [previewEvent, setPreviewEvent] = useState<CalendarEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerTrigger, setDatePickerTrigger] = useState<HTMLElement | null>(null);
  const { todos } = useTodoStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteEventId, setPendingDeleteEventId] = useState<string | null>(null);

  // Check if user has a family, redirect to family creation if not
  // Also refresh family data to ensure we have the latest member colors
  const hasFetchedFamilyRef = useRef(false);

  useEffect(() => {
    // First check: verify authentication and tokens
    const authState = useAuthStore.getState();
    if (!isAuthenticated || !authState.idToken || !authState.accessToken) {
      navigate('/login', { replace: true });
      return;
    }

    if (isAuthenticated && !isFamilyLoading && !hasFetchedFamilyRef.current) {
      hasFetchedFamilyRef.current = true;
      fetchFamily()
        .then(() => {
          const currentFamily = useFamilyStore.getState().family;
          const currentAuthState = useAuthStore.getState();
          if (!currentAuthState.isAuthenticated || !currentAuthState.idToken) {
            navigate('/login', { replace: true });
            return;
          }
          if (currentFamily === null) {
            navigate('/family', { replace: true });
          }
        })
        .catch((error) => {
          console.error('Failed to fetch family:', error);
          const currentAuthState = useAuthStore.getState();
          if (error.message?.includes('Authentication failed') || !currentAuthState.isAuthenticated || !currentAuthState.idToken) {
            navigate('/login', { replace: true });
          }
        });
    }
  }, [isAuthenticated, isFamilyLoading, fetchFamily, navigate]);

  // Fetch events with appropriate date range based on current view
  useEffect(() => {
    if (isAuthenticated && family) {
      // Calculate date range based on current view
      let startDate: string;
      let endDate: string;
      
      if (view === 'month') {
        // Fetch events for the current month view (including partial weeks)
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        // Include partial weeks (6 weeks total = ~42 days)
        const daysToSubtract = firstDay.getDay(); // Days from previous month
        const daysToAdd = 42 - (lastDay.getDate() + daysToSubtract); // Days to next month
        firstDay.setDate(firstDay.getDate() - daysToSubtract);
        lastDay.setDate(lastDay.getDate() + daysToAdd);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
      } else if (view === 'week') {
        // Fetch events for the current week
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - currentDate.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        startDate = weekStart.toISOString().split('T')[0];
        endDate = weekEnd.toISOString().split('T')[0];
      } else {
        // Day view - fetch events for the current day
        const dayStart = new Date(currentDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);
        startDate = dayStart.toISOString().split('T')[0];
        endDate = dayEnd.toISOString().split('T')[0];
      }

      // Prevent duplicate fetches (optional optimization - merging handles duplicates anyway)
      const fetchKey = `${view}-${startDate}-${endDate}`;
      if (lastFetchRef.current && lastFetchRef.current.key === fetchKey) {
        return;
      }

      lastFetchRef.current = { key: fetchKey };
      fetchEvents(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, family, view, currentDate]);

  // Redirect to login if not authenticated or tokens are missing
  const authState = useAuthStore.getState();
  if (!isAuthenticated || !authState.idToken || !authState.accessToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const handlePrevious = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, -1));
    } else if (view === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      // day view
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const handleNext = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      // day view
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };


  const handleDateClick = (date: Date, hour?: number) => {
    // Store the selected date to pre-fill in the wizard
    const initial = new Date(date);
    if (typeof hour === 'number' && Number.isFinite(hour)) {
      initial.setHours(hour, 0, 0, 0);
    }
    setInitialDate(initial);
    setEditingEvent(null);
    setShowCreateEventModal(true);
  };

  const handleNavigateToDay = (date: Date) => {
    setCurrentDate(date);
    setView('day');
  };

  const handleEventClick = (event: CalendarEvent) => {
    // Show event preview instead of directly editing
    setPreviewEvent(event);
    setShowEventPreview(true);
  };

  const handleEditFromPreview = () => {
    if (!previewEvent) return;
    
    const baseId = getBaseEventId(previewEvent.id);
    const eventForEdit = isExpandedRecurringInstance(previewEvent)
      ? { ...previewEvent, id: baseId }
      : previewEvent;

    setEditingEvent(eventForEdit);
    setShowEventPreview(false);
    setShowCreateEventModal(true);
  };

  const handleDeleteFromPreview = () => {
    if (!previewEvent) return;
    
    const baseId = getBaseEventId(previewEvent.id);
    setPendingDeleteEventId(baseId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteEventId) return;
    const toastId = showToast('Deleting event...', 'loading');

    try {
      await deleteEvent(pendingDeleteEventId);
      dismissToast(toastId);
      showToast('Event deleted', 'success');
      setShowEventPreview(false);
      setPreviewEvent(null);
      setShowDeleteConfirm(false);
      setPendingDeleteEventId(null);
    } catch (error: any) {
      dismissToast(toastId);
      showToast(error?.message || 'Failed to delete event', 'error');
      setShowDeleteConfirm(false);
      setPendingDeleteEventId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteEventId(null);
  };

  const isProcessing = isCreating || isUpdating || isDeleting !== null;

  /**
   * Handle event creation/update from wizard form
   * 
   * TIMEZONE HANDLING:
   * - All-day events: Use Date.UTC() to create UTC dates, ensuring the date stays consistent
   *   regardless of user's timezone when stored (toISOString converts to UTC)
   * - Events with time: Use new Date(dateString + timeString) which interprets the time
   *   as LOCAL timezone. When sent to backend via toISOString(), it converts to UTC for storage.
   * - Display: All times are shown in user's local timezone via time formatting utilities
   */
  const handleWizardSubmit = async (formData: EventFormData) => {
    // Determine if this is an Important Date (no time specified or dateType is not regular)
    const isImportantDate = formData.dateType !== 'regular' || !formData.startTime || !formData.endTime;
    const isAllDay = formData.isAllDay === true;
    
    // For Important Dates without times, set full day range
    // For all-day events, also set full day range
    let startDateTime: Date;
    let endDateTime: Date;
    
    if (isImportantDate && (!formData.startTime || !formData.endTime)) {
      // Important Date: full day (00:00:00 to 23:59:59)
      // Use Date.UTC() to create UTC dates - ensures date consistency when stored
      const isSingleDay = formData.startDate === formData.endDate;
      const [startYear, startMonth, startDay] = formData.startDate.split('-').map(Number);
      startDateTime = new Date(Date.UTC(startYear, startMonth - 1, startDay, 0, 0, 0));
      if (isSingleDay) {
        endDateTime = new Date(Date.UTC(startYear, startMonth - 1, startDay, 23, 59, 59));
      } else {
        const [endYear, endMonth, endDay] = formData.endDate.split('-').map(Number);
        endDateTime = new Date(Date.UTC(endYear, endMonth - 1, endDay, 23, 59, 59));
      }
    } else if (isAllDay) {
      // All-day event: full day (00:00:00 to 23:59:59)
      // Use Date.UTC() to create UTC dates - ensures date consistency when stored
      const isSingleDay = formData.startDate === formData.endDate;
      const [startYear, startMonth, startDay] = formData.startDate.split('-').map(Number);
      startDateTime = new Date(Date.UTC(startYear, startMonth - 1, startDay, 0, 0, 0));
      if (isSingleDay) {
        endDateTime = new Date(Date.UTC(startYear, startMonth - 1, startDay, 23, 59, 59));
      } else {
        const [endYear, endMonth, endDay] = formData.endDate.split('-').map(Number);
        endDateTime = new Date(Date.UTC(endYear, endMonth - 1, endDay, 23, 59, 59));
      }
    } else {
      // Event with Time: interpret date+time strings as LOCAL timezone
      // new Date("2024-01-15T14:00") creates a Date object in local timezone
      // When sent to backend via toISOString(), it converts to UTC for storage
      startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);
    }

    // Validate member selection for timed events
    if (!isImportantDate && (!formData.memberIds || formData.memberIds.length === 0)) {
      throw new Error('Please select at least one family member for events with time');
    }

    // Build recurring pattern if recurring
    const recurringPattern: RecurringPattern | undefined = formData.isRecurring
      ? {
          frequency: formData.recurringFrequency,
          interval: formData.recurringInterval,
        }
      : undefined;

    // Auto-set recurring for birthdays/anniversaries
    const isAutoRecurring = formData.dateType === 'birthday' || formData.dateType === 'anniversary';
    const finalIsRecurring = formData.isRecurring || isAutoRecurring;
    const finalRecurringPattern = finalIsRecurring
      ? (recurringPattern || { frequency: 'yearly', interval: 1 })
      : undefined;

    if (editingEvent) {
      // Update existing event
      await updateEvent(editingEvent.id, {
        title: formData.title,
        description: formData.description,
        startTime: startDateTime,
        endTime: endDateTime,
        memberIds: formData.memberIds,
        location: formData.location,
        dateType: formData.dateType,
        priority: formData.priority,
        reminderDays: formData.reminderDays,
        isRecurring: finalIsRecurring,
        recurringPattern: finalRecurringPattern,
        isImportantDate: isImportantDate,
        isAllDay: isAllDay,
      });
      showToast('Event updated', 'success');
    } else {
      // Create new event
      try {
        await createEvent({
          title: formData.title,
          description: formData.description,
          startTime: startDateTime,
          endTime: endDateTime,
          memberIds: formData.memberIds,
          location: formData.location,
          dateType: formData.dateType,
          priority: formData.priority,
          reminderDays: formData.reminderDays,
          isRecurring: finalIsRecurring,
          recurringPattern: finalRecurringPattern,
          isImportantDate: isImportantDate,
          isAllDay: isAllDay,
        });
        showToast('Event created', 'success');
      } catch (error: any) {
        showToast(error?.message || 'Failed to create event', 'error');
      }
    }
    
    // Reset and close
    setEditingEvent(null);
    setShowCreateEventModal(false);
  };

  const handleCloseModal = () => {
    setShowCreateEventModal(false);
    setEditingEvent(null);
    setInitialDate(null);
  };

  // Filter events based on selected member
  const filteredEvents = selectedMemberFilter === null
    ? events
    : events.filter(event => {
        // Support both legacy (single member) and new (multiple members) format
        const matches = (event.memberIds && event.memberIds.length > 0)
          ? event.memberIds.includes(selectedMemberFilter)
          : event.memberId === selectedMemberFilter;
        return matches;
      });

  const theme = {
    bg: tokens.bg.gradient,
    card: `${tokens.card.bg} ${tokens.card.shadow} rounded-landing ${tokens.card.border} border`,
    header: tokens.text.primary,
    accent: tokens.button.primary,
    text: tokens.text.primary,
    border: tokens.border.light,
    button: tokens.button.secondary,
      };

  return (
    <div className={`h-screen ${theme.bg} relative flex flex-col overflow-hidden pr-20`}>
      <NavBar className={`${theme.card} flex-shrink-0`} containerClassName="flex items-center justify-between px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-shrink-0">
          {/* Family Name as Primary */}
          {family?.name && (
            <>
              <div className="flex-shrink-0">
                <div className={`${tokens.text.primary} text-xs sm:text-sm md:text-base font-bold font-serif`}>
                  {family.name}
                </div>
                <div className={`${tokens.text.muted} text-[10px] sm:text-xs`}>Runow</div>
              </div>
              <div className={`h-10 w-px ${tokens.border.light} border-l`}></div>
            </>
          )}
          {/* Desktop view indicator (only visible in development) */}
          {import.meta.env.DEV && (
            <div className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-sm ml-2 flex-shrink-0">
              Desktop
            </div>
          )}
          
          {/* Navigation Controls */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink-0">
            <button
              onClick={handlePrevious}
              className={`${tokens.button.secondary} px-2 sm:px-3 h-10 text-xs sm:text-sm md:text-base min-w-[40px] flex items-center justify-center flex-shrink-0`}
              aria-label="Previous"
            >
              ←
            </button>
            <button
              onClick={handleToday}
              className={`${tokens.button.secondary} px-2 sm:px-3 h-10 text-xs sm:text-sm md:text-base min-w-[60px] sm:min-w-[70px] flex items-center justify-center flex-shrink-0`}
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className={`${tokens.button.secondary} px-2 sm:px-3 h-10 text-xs sm:text-sm md:text-base min-w-[40px] flex items-center justify-center flex-shrink-0`}
              aria-label="Next"
            >
              →
            </button>
          </div>

          {/* View Switcher and Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* View Switcher - Compact on mobile */}
            {(['month', 'week', 'day'] as CalendarView[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-2 sm:px-3 h-10 rounded-landing text-xs sm:text-sm md:text-base font-semibold transition-all active:scale-95 min-w-[45px] sm:min-w-[55px] flex items-center justify-center flex-shrink-0 ${
                  view === v
                    ? `${theme.accent} text-white`
                    : `${tokens.button.secondary} ${tokens.text.primary}`
                }`}
              >
                <span className="hidden sm:inline">{v.charAt(0).toUpperCase() + v.slice(1)}</span>
                <span className="sm:hidden">{v.charAt(0).toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>
      </NavBar>

      {/* Person Filter Tabs - Optimized for Tablet */}
      {family && family.members && family.members.length > 0 && (
        <div className={`${theme.card} flex-shrink-0 mx-2 mt-0 mb-0 overflow-x-auto`}>
          <div className="flex items-center gap-2 sm:gap-3 p-1 sm:p-1.5 min-w-max">
            {/* Create Event Button */}
            <button
              onClick={() => {
                setInitialDate(null); // No pre-selected date when clicking Create button
                setEditingEvent(null);
                setShowCreateEventModal(true);
              }}
              className={`${tokens.button.primary} px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base h-[36px] sm:h-[38px] md:h-[40px] flex items-center justify-center flex-shrink-0 mr-2 whitespace-nowrap`}
            >
              <span className="hidden sm:inline">+ Create</span>
              <span className="sm:hidden">+</span>
            </button>

            {/* All Tab */}
            <button
              onClick={() => setSelectedMemberFilter(null)}
              className={`flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-landing text-xs sm:text-sm md:text-base font-semibold transition-all active:scale-95 h-[36px] sm:h-[38px] md:h-[40px] whitespace-nowrap ${
                selectedMemberFilter === null
                  ? `${theme.accent} text-white shadow-sm`
                  : `${tokens.button.secondary} ${tokens.text.primary}`
              }`}
            >
              {/* Placeholder for avatar to maintain consistent sizing */}
              <div className="relative flex-shrink-0">
                <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gray-400 dark:bg-gray-500">
                  A
                </div>
              </div>
              <span>All</span>
            </button>
            
            {/* Individual Member Tabs */}
            {family.members.map((member) => {
              const isSelected = selectedMemberFilter === member.userId;
              return (
                <button
                  key={member.userId}
                  onClick={() => setSelectedMemberFilter(member.userId)}
                  className={`flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-landing text-xs sm:text-sm md:text-base font-semibold transition-all active:scale-95 h-[36px] sm:h-[38px] md:h-[40px] whitespace-nowrap ${
                    isSelected
                      ? `${theme.accent} text-white shadow-sm`
                      : `${tokens.button.secondary} ${tokens.text.primary}`
                  }`}
                  style={isSelected ? {} : { borderColor: member.color, borderWidth: '2px' }}
                >
                  {/* Member Avatar */}
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: member.color }}
                    >
                      {member.avatar ? null : member.name.charAt(0).toUpperCase()}
                    </div>
                    {member.avatar && (
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className="absolute inset-0 w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full object-cover border-2 border-white z-10"
                        style={{ borderColor: member.color }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <span className="hidden sm:inline">{member.name}</span>
                  <span className="sm:hidden">{member.name.charAt(0).toUpperCase()}</span>
                </button>
              );
            })}

            {/* Current Date Pill - anchored right */}
            <div className="ml-auto flex-shrink-0">
              <div 
                ref={(el) => {
                  if (el && !datePickerTrigger) {
                    setDatePickerTrigger(el);
                  }
                }}
                className={`${tokens.card.bg} ${tokens.text.primary} ${tokens.card.border} border rounded-landing px-2.5 sm:px-3 py-1.5 sm:py-2 h-[36px] sm:h-[38px] md:h-[40px] flex items-center gap-3 sm:gap-4 ${tokens.card.shadow} cursor-pointer transition-all active:scale-95`}
                onClick={(e) => {
                  setDatePickerTrigger(e.currentTarget);
                  setShowDatePicker(true);
                }}
              >
                {isToday(currentDate) && (
                  <span 
                    className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDatePickerTrigger(e.currentTarget.closest('div') as HTMLElement);
                      setShowDatePicker(true);
                    }}
                  >
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    Today
                  </span>
                )}
                <div className="text-lg sm:text-xl md:text-2xl font-bold whitespace-nowrap">
                  {view === 'month'
                    ? formatDate(currentDate, 'month-year')
                    : formatDate(currentDate, 'long')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Calendar Area - Responsive Layout */}
      <div className="flex flex-col lg:flex-row gap-2 sm:gap-3 p-2 flex-1 min-h-0">
        {/* Calendar View */}
        <div className={`${view === 'day' ? 'w-full' : 'flex-1'} ${theme.card} p-1 sm:p-2 overflow-hidden min-h-0 flex flex-col relative`} style={{ height: '100%' }}>
          {view === 'month' && (
            <MonthView
              year={currentDate.getFullYear()}
              month={currentDate.getMonth()}
              events={filteredEvents}
              onDateClick={handleDateClick}
              onEventClick={handleEventClick}
              onNavigateToDay={handleNavigateToDay}
              isDarkMode={isDarkMode}
            />
          )}
          {view === 'week' && (
            <WeekView
              date={currentDate}
              events={filteredEvents}
              onDateClick={handleDateClick}
              onEventClick={handleEventClick}
              isDarkMode={isDarkMode}
            />
          )}
          {view === 'day' && (
            <DayView
              date={currentDate}
              events={filteredEvents}
              onDateClick={handleDateClick}
              onEventClick={handleEventClick}
              isDarkMode={isDarkMode}
            />
          )}
          {/* Loading overlay - only show on initial load when no events are loaded yet */}
          <div
            className={`absolute inset-0 backdrop-blur-sm z-50 pointer-events-none transition-opacity duration-300 ${
              showLoadingOverlay ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundColor: isDarkMode ? 'rgba(217, 119, 111, 0.08)' : 'rgba(217, 119, 111, 0.05)',
            }}
          />
        </div>
      </div>

      <CoreNavRail active="calendar" />

      {/* Event Preview Modal */}
      {showEventPreview && previewEvent && (
        (() => {
          const baseId = getBaseEventId(previewEvent.id);
          const deletingId = previewEvent.isRecurring ? baseId : previewEvent.id;
          return (
            <>
              <EventPreview
                event={previewEvent}
                onEdit={handleEditFromPreview}
                onDelete={handleDeleteFromPreview}
                onClose={() => {
                  setShowEventPreview(false);
                  setPreviewEvent(null);
                }}
                isDarkMode={isDarkMode}
                isDeleting={isDeleting === deletingId}
              />
              <EventTodoPanel eventId={previewEvent.id} eventTitle={previewEvent.title} />
            </>
          );
        })()
      )}

      {/* Delete Confirmation Dialog */}
      {previewEvent && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Event"
          message={
            previewEvent.isRecurring
              ? 'This is a recurring event. Deleting will remove the entire series. Continue?'
              : 'Are you sure you want to delete this event?'
          }
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDestructive={true}
        />
      )}

      {/* Create Event Wizard */}
      <CreateEventWizard
        isOpen={showCreateEventModal}
        onClose={handleCloseModal}
        onSubmit={handleWizardSubmit}
        editingEvent={editingEvent}
        family={family}
        isDarkMode={isDarkMode}
        isProcessing={isProcessing}
        initialDate={initialDate}
      />

      {/* Date Picker Modal */}
      <DatePicker
        isOpen={showDatePicker}
        currentDate={currentDate}
        onDateSelect={(date) => {
          setCurrentDate(date);
          setShowDatePicker(false);
        }}
        onClose={() => setShowDatePicker(false)}
        events={filteredEvents}
        todos={todos}
        isDarkMode={isDarkMode}
        triggerElement={datePickerTrigger}
      />
    </div>
  );
}
